import { useEffect } from "react"

/**
 * OKS-628 — Loads TikTok's `embed.js` so a rendered
 * `<blockquote class="tiktok-embed">` hydrates into the profile card.
 *
 * **Safe render (security floor 90 — TRD KD-4):** the script is appended
 * as a **real `<script>` DOM element** with a **hardcoded** `src` — NEVER
 * injected via a raw-HTML string, and NEVER read from the (external,
 * attacker-influenceable) oEmbed payload. The blockquote markup is
 * separately server-sanitized before it reaches the DOM.
 *
 * Idempotent + SPA-safe:
 *   - The `<script>` is appended once per document (deduped by `src`).
 *     `embed.js` is a global singleton; removing/re-adding it on unmount
 *     thrashes the loader, so there is deliberately no cleanup.
 *   - `embed.js` auto-scans blockquotes on first load. Re-scanning on a
 *     later SPA mount is the CONSUMER's job (it must happen AFTER the new
 *     blockquote is in the DOM) — see `TiktokProfileEmbed`, which calls
 *     {@link rescanTiktokEmbeds} in its mount effect.
 *
 * @param enabled when false (no embed html / fallback state) the hook is a
 *   no-op — we never load the script for a candidate that has no embed.
 */

const EMBED_SCRIPT_SRC = "https://www.tiktok.com/embed.js"

/** Minimal shape `embed.js` exposes on `window` once loaded. */
interface TiktokEmbedGlobal {
  lib?: { render?: (root?: Element | Document) => void }
}

declare global {
  interface Window {
    tiktokEmbed?: TiktokEmbedGlobal
  }
}

/**
 * Ask an already-loaded `embed.js` to (re-)scan `root` for un-hydrated
 * blockquotes. No-op until the library has loaded (first load auto-scans).
 */
export function rescanTiktokEmbeds(root: Element | Document = document): void {
  window.tiktokEmbed?.lib?.render?.(root)
}

export function useTiktokEmbedScript(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return
    if (typeof document === "undefined") return
    if (document.querySelector(`script[src="${EMBED_SCRIPT_SRC}"]`)) return

    const script = document.createElement("script")
    script.src = EMBED_SCRIPT_SRC
    script.async = true
    document.body.appendChild(script)
    // No cleanup — embed.js is a global singleton (see doc comment).
  }, [enabled])
}
